// CalcGlobe analytics — Google Analytics 4 + Google Consent Mode v2.
// Default consent is DENIED for everything. We only update consent to
// "granted" once the user clicks Accept on the cookie banner.
//
// To activate analytics in production, set REACT_APP_GA4_ID in the
// frontend .env (format: "G-XXXXXXXXXX"). If the variable is absent,
// this module is a graceful no-op — no script tags injected, no errors.

const GA4_ID = process.env.REACT_APP_GA4_ID;

let scriptLoaded = false;
let consentModeInitialised = false;

const isBrowser = typeof window !== 'undefined';

function gtag() {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/**
 * Initialise Google Consent Mode v2 with all signals DENIED by default.
 * Must be called BEFORE any tag fires — i.e. at app boot.
 * Safe to call multiple times; only initialises once.
 */
export function initConsentMode() {
  if (!isBrowser || consentModeInitialised) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = gtag;
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });
  consentModeInitialised = true;
}

/**
 * Lazily inject the gtag.js script. Idempotent — running twice is fine.
 * Only runs if REACT_APP_GA4_ID is configured.
 */
function loadScript() {
  if (!isBrowser || scriptLoaded || !GA4_ID) return;
  scriptLoaded = true;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  gtag('js', new Date());
  // We disable automatic page_view so the React Router subscription can
  // send accurate path-based page_view events on SPA navigation.
  gtag('config', GA4_ID, { send_page_view: false });
}

/**
 * Grant analytics + ads consent. Called when the user clicks Accept on
 * the cookie banner. Also injects the gtag script if not already loaded.
 */
export function grantAnalyticsConsent() {
  if (!isBrowser) return;
  initConsentMode();
  gtag('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  });
  loadScript();
}

/**
 * Explicitly deny analytics + ads consent. Called when the user clicks
 * Reject. Updates Consent Mode so any already-loaded gtag stops firing.
 */
export function denyAnalyticsConsent() {
  if (!isBrowser) return;
  initConsentMode();
  gtag('consent', 'update', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
  });
}

/**
 * Send a page_view event to GA4. Called on every React Router navigation.
 * Silent no-op if GA4_ID is not set or gtag hasn't been loaded.
 */
export function trackPageView(path) {
  if (!isBrowser || !GA4_ID || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

export const isAnalyticsConfigured = Boolean(GA4_ID);
